import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import { debounce } from 'lodash';
import trimCanvas from 'trim-canvas';

export type WrittenSignatureProps = {
  name: string;
  shouldRedraw?: boolean;
};

export type WrittenSignatureElement = {
  getBase64Image: () => string;
};

const WrittenSignature = forwardRef<
  WrittenSignatureElement,
  WrittenSignatureProps
>(({ name, shouldRedraw }, ref) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const getBase64Image = () => {
    if (canvasRef.current) {
      const copy = document.createElement('canvas');
      copy.width = canvasRef.current.width;
      copy.height = canvasRef.current.height;

      copy.getContext('2d')?.drawImage(canvasRef.current, 0, 0);
      return trimCanvas(copy).toDataURL();
    }
  };

  useImperativeHandle(ref, () => ({
    getBase64Image,
  }));

  const drawSignature = useCallback(() => {
    if (canvasRef.current) {
      canvasRef.current.width =
        canvasRef.current?.parentElement?.clientWidth ?? 0;
      canvasRef.current.height =
        canvasRef.current?.parentElement?.clientHeight ?? 0;

      const ctx = canvasRef.current.getContext('2d');
      if (ctx) {
        if (window.innerWidth < 700) {
          ctx.font = '32px Sacramento';
        } else {
          ctx.font = '48px Sacramento';
        }
        ctx.textAlign = 'center';

        ctx.fillText(
          name,
          canvasRef.current.width / 2,
          canvasRef.current.height / 2
        ); // draw centered text
      }
    }
  }, [name]);

  useEffect(() => {
    const loadSignatureFont = async () => {
      const myFont = new FontFace(
        'Sacramento',
        "url('assets/fonts/Sacramento-Regular.ttf')"
      );
      const font = await myFont.load();
      document.fonts.add(font);
    };

    const getAdoptSignature = async () => {
      await loadSignatureFont();
      drawSignature();
    };

    getAdoptSignature();
  }, [name, drawSignature]);

  useEffect(() => {
    window.addEventListener('resize', debounce(drawSignature, 700));

    return () => {
      window.removeEventListener('resize', debounce(drawSignature, 700));
    };
  }, [drawSignature]);

  useEffect(() => {
    if (shouldRedraw) {
      setTimeout(() => {
        drawSignature();
      }, 50);
    }
  }, [shouldRedraw, drawSignature]);

  return <canvas ref={canvasRef} />;
});

WrittenSignature.displayName = 'WrittenSignature';

export default WrittenSignature;
